import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  formatRseNumber,
  formatRsePercentage,
} from "../reporteSaludEmergenciaUtils";

const PAGE_SIZE = 25;

const surfaceSx = {
  width: "100%",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
};

const normalizeSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es");

const uniqueSortedValues = (rows, key) =>
  Array.from(new Set(rows.map((row) => row?.[key]).filter(Boolean))).sort((left, right) =>
    String(left).localeCompare(String(right), "es", { sensitivity: "base" })
  );

const numberCellSx = { whiteSpace: "nowrap", textAlign: "right" };

const SummaryMetric = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 900 }}>
      {formatRseNumber(value)}
    </Typography>
  </Box>
);

const PatologiasComunidad = ({
  data,
  status,
  error,
  projectId,
  projectLabel,
  periodLabel,
  onLoad,
}) => {
  const [community, setCommunity] = useState("");
  const [pathology, setPathology] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);

  const rows = useMemo(() => (Array.isArray(data?.rows) ? data.rows : []), [data]);
  const communities = useMemo(() => uniqueSortedValues(rows, "comunidad"), [rows]);
  const pathologies = useMemo(() => uniqueSortedValues(rows, "patologia"), [rows]);

  useEffect(() => {
    setCommunity("");
    setPathology("");
    setSearch("");
    setPage(0);
  }, [data]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = normalizeSearch(search);

    return [...rows]
      .filter((row) => {
        const matchesCommunity = !community || row.comunidad === community;
        const matchesPathology = !pathology || row.patologia === pathology;
        const searchable = normalizeSearch(`${row.comunidad || ""} ${row.patologia || ""}`);
        return matchesCommunity && matchesPathology && (!normalizedSearch || searchable.includes(normalizedSearch));
      })
      .sort(
        (left, right) =>
          Number(right.atencionesPatologia || 0) - Number(left.atencionesPatologia || 0)
      );
  }, [community, pathology, rows, search]);

  useEffect(() => {
    setPage(0);
  }, [community, pathology, search, rowsPerPage]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredRows.length, page, rowsPerPage]);

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const showProjectCode = projectId === null || projectId === "";
  const firstVisibleRow = filteredRows.length === 0 ? 0 : page * rowsPerPage + 1;
  const lastVisibleRow = Math.min((page + 1) * rowsPerPage, filteredRows.length);

  return (
    <Paper
      component="section"
      data-testid="patologias-comunidad-panel"
      aria-labelledby="patologias-comunidad-title"
      sx={{ ...surfaceSx, p: { xs: 2, md: 2.5 } }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
      >
        <Box>
          <Typography id="patologias-comunidad-title" variant="h5" sx={{ fontWeight: 850 }}>
            Patologías por comunidad
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Proyecto: <strong>{projectLabel}</strong> · Período: <strong>{periodLabel}</strong>
          </Typography>
        </Box>

        {status !== "loading" && (
          <Button
            variant={status === "idle" ? "contained" : "outlined"}
            startIcon={status === "error" ? <RefreshOutlinedIcon /> : <VisibilityOutlinedIcon />}
            onClick={onLoad}
          >
            {status === "error" ? "Reintentar" : status === "loaded" ? "Actualizar detalle" : "Cargar detalle"}
          </Button>
        )}
      </Stack>

      {status === "idle" && (
        <Box sx={{ minHeight: 110, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
          <Typography color="text.secondary">
            Detalle no cargado. Utiliza los mismos filtros de proyecto y período seleccionados arriba.
          </Typography>
        </Box>
      )}

      {status === "loading" && (
        <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ minHeight: 150 }}>
          <CircularProgress size={32} aria-label="Cargando detalle de patologías por comunidad" />
          <Typography color="text.secondary">Cargando detalle...</Typography>
        </Stack>
      )}

      {status === "error" && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || "No fue posible cargar el detalle de patologías por comunidad."}
        </Alert>
      )}

      {status === "loaded" && rows.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No hay información de patologías por comunidad para los filtros seleccionados.
        </Alert>
      )}

      {status === "loaded" && rows.length > 0 && (
        <>
          <Grid
            container
            spacing={2}
            sx={{ mt: 1, mb: 2, py: 1.5, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Grid item xs={4} sm="auto">
              <SummaryMetric label="Comunidades representadas" value={communities.length} />
            </Grid>
            <Grid item xs={4} sm="auto">
              <SummaryMetric label="Patologías representadas" value={pathologies.length} />
            </Grid>
            <Grid item xs={4} sm="auto">
              <SummaryMetric label="Combinaciones" value={rows.length} />
            </Grid>
          </Grid>

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Comunidad"
                value={community}
                onChange={(event) => setCommunity(event.target.value)}
              >
                <MenuItem value="">Todas las comunidades</MenuItem>
                {communities.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Patología"
                value={pathology}
                onChange={(event) => setPathology(event.target.value)}
              >
                <MenuItem value="">Todas las patologías</MenuItem>
                {pathologies.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                fullWidth
                size="small"
                label="Buscar comunidad o patología"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Mostrando {firstVisibleRow}-{lastVisibleRow} de {formatRseNumber(filteredRows.length)} combinaciones
          </Typography>

          <TableContainer sx={{ maxHeight: 560, border: "1px solid", borderColor: "divider" }}>
            <Table stickyHeader size="small" aria-label="Patologías por comunidad">
              <TableHead>
                <TableRow>
                  {showProjectCode && <TableCell sx={{ fontWeight: 900 }}>Código proyecto</TableCell>}
                  <TableCell sx={{ fontWeight: 900 }}>Comunidad</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Patología</TableCell>
                  <TableCell sx={{ ...numberCellSx, fontWeight: 900 }}>Atenciones</TableCell>
                  <TableCell sx={{ ...numberCellSx, fontWeight: 900 }}>Personas únicas</TableCell>
                  <TableCell sx={{ ...numberCellSx, fontWeight: 900 }}>Mujeres</TableCell>
                  <TableCell sx={{ ...numberCellSx, fontWeight: 900 }}>Hombres</TableCell>
                  <TableCell sx={{ ...numberCellSx, fontWeight: 900 }}>Sexo sin dato</TableCell>
                  <TableCell sx={{ ...numberCellSx, fontWeight: 900 }}>Aprobadas</TableCell>
                  <TableCell sx={{ ...numberCellSx, fontWeight: 900 }}>Atenciones comunidad</TableCell>
                  <TableCell sx={{ ...numberCellSx, fontWeight: 900 }}>% dentro comunidad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow
                    hover
                    key={`${row.projectId}-${row.comunidad}-${row.pathologyId}`}
                    data-testid="patologia-comunidad-row"
                  >
                    {showProjectCode && <TableCell sx={{ whiteSpace: "nowrap" }}>{row.codigo || "Sin código"}</TableCell>}
                    <TableCell>{row.comunidad}</TableCell>
                    <TableCell>{row.patologia}</TableCell>
                    <TableCell sx={numberCellSx}>{formatRseNumber(row.atencionesPatologia)}</TableCell>
                    <TableCell sx={numberCellSx}>{formatRseNumber(row.personasUnicas)}</TableCell>
                    <TableCell sx={numberCellSx}>{formatRseNumber(row.mujeres)}</TableCell>
                    <TableCell sx={numberCellSx}>{formatRseNumber(row.hombres)}</TableCell>
                    <TableCell sx={numberCellSx}>{formatRseNumber(row.sexoSinDato)}</TableCell>
                    <TableCell sx={numberCellSx}>{formatRseNumber(row.aprobadas)}</TableCell>
                    <TableCell sx={numberCellSx}>{formatRseNumber(row.atencionesComunidad)}</TableCell>
                    <TableCell sx={numberCellSx}>{formatRsePercentage(row.porcentajeDentroComunidad)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}
    </Paper>
  );
};

export default PatologiasComunidad;
